import React, { Component } from 'react';
import {
  inject,
  observer,
} from 'mobx-react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import Button from 'react-bootstrap/lib/Button';
import pick from 'lodash/pick';
import {
  createTimerRegistry,
  timerKey,
} from '../../../utils/timer-manager/timer-manager';
import './PushButton.css';

// Shared timer registry for all PushButton instances
const pushButtonTimers = createTimerRegistry('PushButton');

@inject('batchStore')
@observer
class PushButton extends Component {
  constructor() {
    super();
    this.state = {
      active: false,
      rightClickHoldActive: false,
    };
    this.lastPressTime = 0;
  }

  getRightClickHoldTimerKey = () => {
    const { port } = this.props;
    return timerKey.custom('pushbutton', 'rightClickHold', port || 'unknown');
  };

  supportsTouch = () => 'ontouchstart' in window;

  onDown = e => {
    // Right-click is handled separately
    if (e.button === 2) {
      return;
    }

    // Debounce to prevent multiple rapid presses
    const now = Date.now();
    if (now - this.lastPressTime < 200) {
      return;
    }
    this.lastPressTime = now;

    const { batchStore, onPush = () => {} } = this.props;
    // stop bubbling to prevent unexpected dragging
    if (!batchStore.canSendRemoteInstruction()) {
      e.stopPropagation();
    }

    this.setState({ active: true });
    onPush();

    this.isDown = true;
    const event = this.supportsTouch() ? 'touchend' : 'mouseup';
    window.addEventListener(event, this.onUp);
  };

  onRightMouseDown = e => {
    // Only handle right-click (button 2)
    if (e.button !== 2) {
      return;
    }

    const { disabled, onRightClickHoldStart, onRightClickHoldEnd, rightClickHoldDuration = 1500 } = this.props;
    if (disabled || !onRightClickHoldEnd) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    this.setState({ active: true, rightClickHoldActive: true });

    if (onRightClickHoldStart) {
      onRightClickHoldStart();
    }

    const timerKey = this.getRightClickHoldTimerKey();
    pushButtonTimers.set(
      timerKey,
      () => {
        this.setState({ active: false, rightClickHoldActive: false });
        onRightClickHoldEnd();
        window.removeEventListener('mouseup', this.onRightClickHoldRelease);
      },
      rightClickHoldDuration,
      'Right-click hold completion',
    );

    window.addEventListener('mouseup', this.onRightClickHoldRelease);
  };

  onUp = e => {
    if (!this.isDown) {
      return;
    }

    const { port, batchStore, onPushUp = () => {}, setOutputOnRelease = true } = this.props;
    // stop bubbling to prevent unexpected dragging.
    if (!batchStore.canSendRemoteInstruction()) {
      e.stopPropagation();
    }

    this.setState({ active: false });

    if (port && setOutputOnRelease) {
      batchStore.setOutput(port, '0');
    }

    onPushUp();

    this.isDown = false;

    const event = this.supportsTouch() ? 'touchend' : 'mouseup';
    window.removeEventListener(event, this.onUp);
  };

  onContextMenu = e => {
    const { onRightClickHoldEnd } = this.props;
    // Prevent context menu when right-click hold is configured
    if (onRightClickHoldEnd) {
      e.preventDefault();
    }
  };

  onRightClickHoldRelease = () => {
    const timerKey = this.getRightClickHoldTimerKey();

    // If timer is still running, hold wasn't completed
    if (pushButtonTimers.has(timerKey)) {
      pushButtonTimers.clear(timerKey);
      this.setState({ active: false, rightClickHoldActive: false });
    }

    window.removeEventListener('mouseup', this.onRightClickHoldRelease);
  };

  componentWillUnmount() {
    if (this.isDown) {
      const event = this.supportsTouch() ? 'touchend' : 'mouseup';
      window.removeEventListener(event, this.onUp);
    }
    const timerKey = this.getRightClickHoldTimerKey();
    pushButtonTimers.clear(timerKey);
    window.removeEventListener('mouseup', this.onRightClickHoldRelease);
  };

  render() {
    const { className, active: propActive, isContinuousPressActive, continuousPressDuration, rightClickHoldDuration, onRightClickHoldLabel, port, batchStore } = this.props;
    const { active: stateActive, rightClickHoldActive } = this.state;

    // Reflect actual port state in button appearance
    const isPortActive = port && batchStore.isOutputOn(port);

    // Determine which duration to use for CSS animation
    const animationDuration = rightClickHoldDuration || continuousPressDuration || 3000;

    // Show progress ring if either:
    // - Parent controls it via isContinuousPressActive (existing Bin usage)
    // - Right-click hold is in progress (new WaterScale usage)
    const showProgressRing = isContinuousPressActive || rightClickHoldActive;

    const extraClassName = cx(className || 'btn-batch btn-batch-green', {
      'active': stateActive || propActive || isPortActive,
      'continuous-press-loading': showProgressRing,
    });
    const allowedProps = pick(this.props, ['bsStyle', 'bsSize']);

    const style = {};
    if (animationDuration) {
      style['--continuous-press-duration'] = `${animationDuration}ms`;
    }

    return (
      <div className="push-button-wrapper">
        {rightClickHoldActive && onRightClickHoldLabel && (
          <div className="right-click-hold-label">{onRightClickHoldLabel}</div>
        )}
        <Button
          bsSize="sm"
          bsStyle="primary"
          active={stateActive || propActive || isPortActive}
          disabled={this.props.disabled || false}
          className={extraClassName}
          style={style}
          {...allowedProps}
          onMouseDown={this.supportsTouch() ? () => {} : (e) => { this.onDown(e); this.onRightMouseDown(e); }}
          onMouseUp={this.supportsTouch() ? () => {} : this.onUp}
          onTouchStart={this.supportsTouch() ? this.onDown : () => {}}
          onTouchEnd={this.supportsTouch() ? this.onUp : () => {}}
          onContextMenu={this.onContextMenu}
        >
          {showProgressRing && (
            <svg className="progress-ring" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="45" />
              <circle className="progress" cx="50" cy="50" r="45" />
            </svg>
          )}
          {this.props.children || <i className="fa fa-arrow-down" />}
        </Button>
      </div>
    );
  }
}

PushButton.propTypes = {
  onPush: PropTypes.func,
  onPushUp: PropTypes.func,
  active: PropTypes.bool,
  setOutputOnRelease: PropTypes.bool,
  // Parent-controlled progress indicator (existing Bin usage)
  isContinuousPressActive: PropTypes.bool,
  continuousPressDuration: PropTypes.number,
  // Right-click hold callbacks
  onRightClickHoldStart: PropTypes.func,
  onRightClickHoldEnd: PropTypes.func,
  rightClickHoldDuration: PropTypes.number,
  onRightClickHoldLabel: PropTypes.string,
};

export default PushButton;
